'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  Award,
  Bell,
  Calendar,
  FileText,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Settings,
  Ticket,
  User,
  UserPlus,
  Users,
} from 'lucide-react';
import { SidebarBrandLogo } from '@/components/layout/SidebarBrandLogo';
import { startNavigationLoadingForHref } from '@/lib/navigation-loading';
import { useAuthStore } from '@/lib/auth-store';
import { getEffectiveRole } from '@/lib/rbac';

type NavItem = {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

const SECTIONS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Main',
    items: [{ href: '/dashboard', label: 'Overview', icon: LayoutDashboard }],
  },
  {
    title: 'MY ACTIVITY',
    items: [
      { href: '/dashboard/profile', label: 'Profile', icon: User },
      { href: '/dashboard/my-events', label: 'My Events', icon: Calendar },
      { href: '/dashboard/my-tickets', label: 'Tickets', icon: Ticket },
      { href: '/dashboard/my-submissions', label: 'Submissions', icon: FileText },
    ],
  },
  {
    title: 'Account',
    items: [
      { href: '/dashboard/messages', label: 'Messages', icon: MessageSquare },
      { href: '/dashboard/notifications', label: 'Notifications', icon: Bell },
      { href: '/dashboard/settings', label: 'Settings', icon: Settings },
    ],
  },
];

function isNavActive(pathname: string | null, href: string) {
  const current = (pathname || '/').replace(/\/$/, '') || '/';
  const target = href.replace(/\/$/, '') || '/';
  if (target === '/dashboard') return current === '/dashboard';
  return current === target || current.startsWith(`${target}/`);
}

export function ParticipantSidebar() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, logout } = useAuthStore();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  const role = getEffectiveRole(user);

  useEffect(() => {
    setPendingHref(null);
  }, [pathname]);

  const markPending = (href: string) => {
    const current = (pathname || '/').replace(/\/$/, '') || '/';
    const target = href.replace(/\/$/, '') || '/';
    if (target !== current) {
      startNavigationLoadingForHref(href, pathname);
      setPendingHref(href);
    }
  };

  return (
    <aside className="eh-sidebar ud-sidebar w-full overflow-hidden" aria-label="User dashboard">
      <Link href="/" className="block px-5 pt-6 pb-4" aria-label="EnthiosHub home">
        <SidebarBrandLogo />
      </Link>

      <div ref={scrollRef} className="eh-sidebar-scroll custom-scrollbar pb-3 px-1">
        {SECTIONS.map((section) => (
          <div key={section.title} className="mb-1">
            <p className="ud-nav-label">{section.title}</p>
            <nav className="space-y-0.5" aria-label={section.title}>
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = isNavActive(pendingHref || pathname, item.href);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    prefetch={false}
                    aria-current={active ? 'page' : undefined}
                    onClick={() => markPending(item.href)}
                    className="ud-nav-item"
                  >
                    <Icon className="w-5 h-5 shrink-0 text-text-inverse/85" aria-hidden />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        ))}
      </div>

      {user ? (
        <div className="shrink-0 border-t border-white/10 px-3 py-3">
          <div className="flex items-center gap-2">
            <Link
              href="/dashboard/profile"
              className="min-w-0 flex-1 flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-bg/8 transition-colors duration-150"
            >
              <div className="h-9 w-9 rounded-full bg-[var(--color-primary)]/25 flex items-center justify-center text-sm font-bold uppercase text-text-inverse">
                {(user.name || user.email || 'U').slice(0, 1)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-text-inverse truncate">{user.name || 'Account'}</p>
                <p className="text-[11px] text-text-inverse/72 truncate capitalize">{role || 'Member'}</p>
              </div>
            </Link>
            <button
              type="button"
              aria-label="Sign out"
              onClick={async () => {
                try {
                  await logout();
                } catch {
                  /* still leave */
                }
                window.location.href = '/login?reason=logged_out';
              }}
              className="p-2 rounded-lg text-text-inverse/45 hover:text-text-inverse hover:bg-bg/10 transition-colors duration-150"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}
